import React, { useEffect, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSpinnerSmall from "../../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSpinner from "../../../../ui-kit/kuika-spinner/kuika-spinner";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

type SpinnerSmallSize = "small" | "default" | "large";

const SPINNER_SMALL_SIZES: SpinnerSmallSize[] = ["small", "default", "large"];

const OverlayDemo: React.FC = (): JSX.Element => {
  const [active, setActive] = useState<boolean>(false);

  useEffect((): (() => void) | void => {
    if (!active) return;
    const timer: number = window.setTimeout(() => setActive(false), 2000);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setActive(false);
    };
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <>
      <KuikaButton type="secondary" size="sm" onClick={() => setActive(true)}>
        Trigger
      </KuikaButton>
      {active && (
        <div onClick={() => setActive(false)} role="presentation">
          <KuikaSpinner spinning />
        </div>
      )}
    </>
  );
};

interface IStageBoxProps {
  children: React.ReactNode;
  height?: number;
}

const StageBox: React.FC<IStageBoxProps> = ({ children, height = 120 }: IStageBoxProps) => (
  <div
    style={{
      position: "relative",
      width: 220,
      height,
      border: "1px dashed var(--lab-border)",
      background: "var(--lab-paper-alt)",
      overflow: "hidden"
    }}
  >
    {children}
  </div>
);

export const SpinnerStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Spinner"
      componentName="KuikaSpinner · KuikaSpinnerSmall · KuikaSmallSpinner"
      sourcePath="ui-kit/kuika-spinner"
      description="The Kuika logo spinners: a full-screen overlay (KuikaSpinner) for a route or a context switch, and a wrapper that dims and locks its content (KuikaSpinnerSmall). KuikaSmallSpinner is KuikaLoader's ring under its old name — see Loader. Under reduced motion the logo stops turning and breathes. KuikaSpinner's size prop is deprecated — every size draws the same 120px glyph."
    >
      <SpecRow
        label="KuikaSpinner — full-screen overlay"
        caption="position:fixed overlay. Click anywhere or press Esc to dismiss · auto-closes after 2s"
        align="start"
      >
        <OverlayDemo />
      </SpecRow>
      <SpecRow
        label="KuikaSpinnerSmall"
        caption="wraps content; while spinning the content is dimmed and takes no clicks. Glyph now fits its box at every size."
        align="start"
      >
        {SPINNER_SMALL_SIZES.map((size: SpinnerSmallSize) => (
          <StageBox key={size} height={100}>
            <KuikaSpinnerSmall spinning size={size}>
              <div style={{ padding: 16, color: "var(--lab-ink-muted)" }}>{size}</div>
            </KuikaSpinnerSmall>
          </StageBox>
        ))}
        <StageBox height={100}>
          <KuikaSpinnerSmall spinning={false}>
            <div style={{ padding: 16, color: "var(--lab-ink-muted)" }}>Idle — spinning=false</div>
          </KuikaSpinnerSmall>
        </StageBox>
      </SpecRow>
      <SpecRow label="KuikaSmallSpinner" caption="KuikaLoader ring in brand blue">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <KuikaSmallSpinner />
          <span style={{ color: "var(--lab-ink-muted)" }}>Saving…</span>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <KuikaSmallSpinner />
          <KuikaSmallSpinner />
          <KuikaSmallSpinner />
        </span>
      </SpecRow>
    </ShowcaseFrame>
  );
};
